import { useState, useEffect } from 'react';
import { getFirestore, doc, getDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';

export interface UserProfile {
  id: string;
  fullName?: string;
  displayName?: string;
  email?: string;
  photoURL?: string;
  role?: string;
  oneLiner?: string;
  bio?: string;
  bioTitle?: string;
  bioQuote?: string;
  birthYear?: string;
  revenue?: string;
  companies?: string;
  coreValue1?: string;
  coreValue1Subtitle?: string;
  coreValue2?: string;
  coreValue2Subtitle?: string;
  coreValue3?: string;
  coreValue3Subtitle?: string;
  expertise?: string[];
  interests?: string[];
  languages?: string[];
  company?: string;
  companyDescription?: string;
  companyImage?: string;
  companyVideo?: string;
  companyLogo?: string;
  companyLocation?: string;
  kvkNumber?: string;
  phone?: string;
  website?: string;
  linkedin?: string;
  instagram?: string;
  youtube?: string;
  twitter?: string;
  facebook?: string;
  tiktok?: string;
  spotify?: string;
  github?: string;
  socialVisibility?: {
    website?: boolean;
    linkedin?: boolean;
    instagram?: boolean;
    youtube?: boolean;
    twitter?: boolean;
    facebook?: boolean;
    tiktok?: boolean;
    spotify?: boolean;
    github?: boolean;
  };
  accentColor?: string;
  missie?: string;
  visie?: string;
  activities?: Array<{
    title: string;
    subtitle: string;
    description: string;
  }>;
  services?: Array<{
    id?: string;
    title: string;
    description: string;
    price?: string;
    imageUrl?: string;
    link?: string;
  }>;
  [key: string]: any;
}

export interface CarouselCard {
  id: string;
  title: string;
  subtitle?: string;
  description?: string;
  image?: string;
  heroBackgrounds?: string[];
  expertise?: string[];
  interesses?: string[];
  [key: string]: any;
}

export const useMijnVerhaalData = (userId?: string | null) => {
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [carouselCards, setCarouselCards] = useState<CarouselCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const db = getFirestore();
        
        // If no userId provided, use current user
        let targetUserId = userId;
        if (!targetUserId) {
          const auth = getAuth();
          targetUserId = auth.currentUser?.uid || null;
        }
        
        if (!targetUserId) {
          setError('Geen gebruiker geselecteerd');
          setLoading(false);
          return;
        }

        // Fetch user profile
        const userDocRef = doc(db, 'userProfiles', targetUserId);
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists()) {
          setUserProfile({ id: userDoc.id, ...userDoc.data() } as UserProfile);
        } else {
          setError('Profiel niet gevonden');
        }

        // Fetch carousel cards
        const cardsQuery = query(
          collection(db, 'carouselCards'),
          where('userId', '==', targetUserId)
        );
        const cardsSnapshot = await getDocs(cardsQuery);
        const cards = cardsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as CarouselCard[];

        setCarouselCards(cards);
      } catch (err: any) {
        console.error('Error fetching profile data:', err);
        setError(err.message || 'Er is een fout opgetreden');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [userId]);

  return { userProfile, carouselCards, loading, error };
};
